import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { Table } from "../../../../../../components/Functional";
import { FETCH_OPTIMISED_ITEMS_V2 as fetchItemsV2 } from "../../../../../../graphql/query/Item";
import { formatCurrency, formatDate } from "../../../../../../util";
import { formatInvoiceReference } from "../../../../../../util/formatInvoiceReference";
import useTooltip from "../../../../../../hooks/useTooltip";
import SectionHeader from "../../components/SectionHeader";

// Both invoices and proformas are shown on the Contact Sales tab. They
// share the same invoice table and are distinguished by `typeId`
// (1 = Invoice, 2 = Proforma); the Invoice NO. column below prefixes
// proformas with "PF" so they can be told apart at a glance.
export const buildSalesQueryVariables = contactId => ({
  _invoice: {
    contactId: {
      operator: "eq",
      value: contactId,
    },
  },
  _instance: {
    id: {
      operator: "ne",
      value: null,
    },
  },
  typeId: {
    operator: "in",
    value: [6, 8],
  },
});

const ConversionTooltip = ({ values }) => {
  const {
    invoice,
    adjustedPrice,
    priceGBP,
    exchange,
    gbpExchangeRate,
    gbpRateEstimated,
    companyBaseCurrency,
  } = values || {};
  const currencyCode = invoice?.currency?.code;
  const invoiceDate = invoice?.date;
  const isGBP = currencyCode === "GBP";
  const baseAmount =
    exchange && adjustedPrice ? adjustedPrice / exchange : null;

  return (
    <div className="w-72 rounded-lg bg-gray-900 p-4 text-sm text-white shadow-xl">
      <div className="mb-3 border-b border-gray-700 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        Conversion Breakdown
      </div>
      {isGBP ? (
        <div className="text-gray-300">
          Invoiced in GBP — no conversion needed
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="font-medium text-white">
              {currencyCode} {formatCurrency(adjustedPrice)}
            </span>
            <span className="text-xs text-gray-400">Invoiced</span>
          </div>

          <div className="flex flex-col gap-0.5 pl-2">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className="text-gray-600">↓</span>
              <span>
                Rate: {exchange ?? "missing"}{" "}
                <span className="text-gray-500">
                  ({formatDate(invoiceDate)})
                </span>
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="font-medium text-white">
              {companyBaseCurrency || "?"} {formatCurrency(baseAmount)}
            </span>
            <span className="text-xs text-gray-400">Company Base</span>
          </div>

          <div className="flex flex-col gap-0.5 pl-2">
            <div
              className={`flex items-center gap-1.5 text-xs ${gbpRateEstimated ? "text-amber-400" : "text-gray-400"}`}
            >
              <span className="text-gray-600">↓</span>
              <span>
                Rate: {gbpExchangeRate ?? "missing"}{" "}
                {gbpRateEstimated ? (
                  <span className="text-amber-400">(est.)</span>
                ) : (
                  <span className="text-gray-500">
                    ({formatDate(invoiceDate)})
                  </span>
                )}
              </span>
            </div>
            {gbpRateEstimated && (
              <div className="mt-0.5 text-xs text-amber-400/80">
                No historical rate for this date — using latest available
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-700 pt-2">
            <span className="font-semibold text-white">
              GBP {formatCurrency(priceGBP)}
            </span>
            <span className="text-xs text-gray-400">Converted</span>
          </div>
        </div>
      )}
    </div>
  );
};

const PriceGBPCell = props => {
  const { priceGBP, gbpRateEstimated } = props?.values || {};
  const { showTooltip, tooltipPos, handleMouseEnter, handleMouseLeave } =
    useTooltip();

  return (
    <>
      <div
        className={`inline-block cursor-pointer border-b border-dashed ${gbpRateEstimated ? "border-amber-400 text-amber-600" : "border-gray-400"}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {formatCurrency(priceGBP)}
        {gbpRateEstimated ? " *" : ""}
      </div>
      {showTooltip &&
        tooltipPos &&
        createPortal(
          <div
            className="pointer-events-none fixed z-50"
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            <ConversionTooltip values={props?.values} />
          </div>,
          document.body,
        )}
    </>
  );
};

const TotalSpendFooter = props => {
  const { totalSpend = 0.0 } = props || {};
  return (
    <div className="border-b pb-4 pl-[1170px] pt-4 font-bold">
      Total Spend: {formatCurrency(totalSpend)}
    </div>
  );
};

const ContactSales = props => {
  const { contactId } = props || {};
  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "itemsV2",
    headers: [
      {
        label: "Image",
        name: "instance?.edition?.artwork?.imagesSummary?.imgT",
        type: "image",
      },
      {
        label: "Invoice NO.",
        type: "custom",
        component: props => {
          const { invoice } = props?.values || {};
          return (
            <Link className="underline" to={`/invoice/${invoice?.id}/details`}>
              {formatInvoiceReference(invoice)}
            </Link>
          );
        },
      },
      {
        label: "Transaction Type",
        type: "custom",
        component: props => {
          const { typeId } = props?.values || {};
          return <div>{typeId === 6 ? "Credit Note" : "Sale"}</div>;
        },
      },
      {
        label: "Artist",
        type: "custom",
        component: props => {
          return (
            <div
              className={`overflow-hidden text-ellipsis text-base font-medium leading-5 text-black`}
              style={{ maxWidth: 250 }}
            >
              {(
                props?.values?.instance?.edition?.artwork?.artists?.map(
                  artist => artist?.name,
                ) || []
              )?.join(", ")}
            </div>
          );
        },
      },
      { label: "Title", name: "instance.edition.artwork.title", type: "label" },
      { label: "Date", name: "invoice.date", type: "date" },
      { label: "Currency", name: "invoice.currency.code", type: "label" },
      { label: "Price", name: "adjustedPrice", type: "currency" },
      {
        label: "Price (GBP)",
        type: "custom",
        component: PriceGBPCell,
      },
    ],
    inputs: [
      { label: "Date From", name: "from", type: "date", view: "partial" },
      { label: "Date To", name: "to", type: "date", view: "partial" },
    ],
    footerComponent: TotalSpendFooter,
    FETCH_QUERY: fetchItemsV2({
      instance: {
        edition: { artwork: { artists: {}, imagesSummary: { imgT: {} } } },
      },
      invoice: { currency: {}, type: {} },
    }),
    variables: buildSalesQueryVariables(contactId),
    isSubTable: true,
  };

  return (
    <div className="w-full">
      <SectionHeader title="Sales" />
      <div className="mb-6 w-full">
        <Table {...tableProps} />
      </div>
    </div>
  );
};

export default ContactSales;
